import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../util_components/button';
import CustomCheckBox from '../../../util_components/custom_checkbox';
import CustomRadioButton from '../../../util_components/custom_radio_button';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import TeacherCancelBucketPicker from '../../../util_components/teacher_cancel_bucket_picker';
import { CareRequestFields, CareSaveRetry } from '../../../util_components/care_request';
import { FormActions } from '../../../util_components/form_layout';
import {
  IST,
  KIND_CANCEL,
  build_create_payload,
  care_api_error
} from '../../../util_components/care_request/care_request_rules';
import { post_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { session_status_values } from '../../../../constants';
import EditReasonField from './edit_reason_field';
import RequestTimeWarningStep from '../../../util_components/request_time_warning';
import useRequestTimeWarning from '../../../util_components/request_time_warning/use_request_time_warning';
import {
  client_request_time_warning,
  session_booked_at,
  with_request_time_ack
} from '../../../util_components/request_time_warning/request_time_warning_rules';
import { CANCEL_EMAIL_NOTE_LABEL, shows_cancel_email_note } from './edit_reason_rules';
import useCareCapture from './use_care_capture';
import useCareCancelRecord from './use_care_cancel_record';
import {
  CARE_OWNS_CANCEL_TIME_NOTE,
  cancel_flags_to_send,
  care_owns_cancel_time,
  edit_field_patch,
  edit_form_from,
  edit_reason_of,
  group_cancel_flags_sent,
  group_cancel_options,
  group_cancel_pick_patch,
  GROUP_CANCEL_LABELS,
  GROUP_CANCEL_TEACHER,
  ignore_reason_patch,
  is_cancel_capture,
  is_new_teacher_cancel,
  plan_edit_save,
  sent_request_time,
  shows_student_app_cancel,
  stored_student_cancel_note,
  teacher_flag_sent,
  toggle_cancel_patch,
  update_session_error_text,
  update_session_payload,
  uses_group_cancel_picks
} from './session_change_rules';

const cancel_options = ['cancelled_by_student', 'cancelled_by_teacher'];

// The session page's "Edit Session Details" body; mounted per open, so it starts from the loaded session.
// Ignore-rating and the page loading flag stay on the page, which seeds them from each load.
function EditSessionForm(props) {
  const { session_uuid, session_details, loading, ignore_rating, ignore_rating_reason } = props;
  const [form, setForm] = useState(() => edit_form_from(session_details));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const care = useCareCapture({
    session_uuid,
    kind: KIND_CANCEL,
    on_saved: props.on_care_saved,
    on_retry_change: props.on_retry_change
  });
  const care_record = useCareCancelRecord(session_uuid, session_details);
  const rt = useRequestTimeWarning();
  const [rt_local, set_rt_local] = useState(''); // the request time the warning is about
  const booked_at = session_booked_at(session_details);

  const on_field = (e) => set(edit_field_patch(e.target.name, e.target.value));
  const on_ignore_reason = (e) => {
    props.on_page_field_change(e);
    set(ignore_reason_patch());
  };
  const patch_care = (patch) => {
    care.patch(patch);
    if ('requested_at' in patch) set({ cancel_bucket_error: false });
  };

  // ack: the request-time acknowledgement from the warning step (A-52); null on the first save
  const save = async (ack = null) => {
    const plan = plan_edit_save({
      form,
      session_details,
      care_draft: care.draft,
      ignore_rating,
      ignore_rating_reason,
      now_ms: Date.now(),
      care_record
    });
    if (plan.errors) {
      set(plan.errors);
      if (plan.care_errors) care.setErrors(plan.care_errors);
      return;
    }
    const requested_local = sent_request_time({
      form,
      plan,
      care_draft: care.draft,
      session_type: session_details.session_type
    });
    set_rt_local(requested_local);
    const pre =
      !ack && requested_local
        ? client_request_time_warning({
            requested_local,
            class_start: session_details.epoch_start_time,
            booked_at
          })
        : null;
    if (pre) {
      rt.show(pre);
      return;
    }
    props.set_loading(true);
    set({ save_modal_error: '' });
    let screenshot_key = '';
    try {
      screenshot_key = plan.capture ? await care.upload_once() : '';
    } catch (e) {
      props.set_loading(false);
      props.notify(
        'error',
        care_api_error(e, 'The screenshot could not be uploaded. Nothing was changed.').message
      );
      return;
    }
    const payload = with_request_time_ack(
      update_session_payload({
        session_uuid,
        student_uuid: session_details.student_uuid,
        form,
        plan,
        ignore_rating,
        ignore_rating_reason
      }),
      ack
    );
    post_api(get_api_url('update_session'), payload, true)
      .then(() => {
        if (!plan.capture) {
          props.on_saved();
          return;
        }
        care.save_after_change(
          with_request_time_ack(
            build_create_payload(care.draft, {
              session_uuid,
              note: plan.edit.reason,
              initiated_by: plan.initiator,
              screenshot_key
            }),
            ack
          ),
          props.on_saved
        );
      })
      .catch((e) => {
        if (rt.catch_warning(e)) {
          props.on_save_failed();
          return;
        }
        // the modal stays open with the server's reason inline (e.g. the slot is taken)
        props.on_save_failed();
        set({ save_modal_error: update_session_error_text(e, 'Failed to update session') });
      });
  };

  if (care.retry) {
    return (
      <CareSaveRetry
        message={care.retry.message}
        busy={care.retry.busy}
        warning={care.retry.warning}
        requestedAt={care.retry.payload.requested_at}
        onRetry={care.retry_save}
        onClose={props.on_close}
      />
    );
  }
  if (loading) return null;
  if (rt.warning) {
    return (
      <RequestTimeWarningStep
        warning={rt.warning}
        requestedLocal={rt_local}
        onAcknowledge={(ack) => {
          rt.clear();
          save(ack);
        }}
        onChangeTime={rt.clear}
      />
    );
  }
  const care_owns_time = care_owns_cancel_time(form, session_details, care_record);
  const edit = edit_reason_of(form, session_details, care_owns_time);
  const capture = is_cancel_capture(form.status, session_details, care.draft);
  const is_group = uses_group_cancel_picks(session_details.session_type);
  const group_options = group_cancel_options(session_details.session_type);
  const picker = {
    sessionStartEpoch: session_details.epoch_start_time,
    ianaTimezone: IST,
    timezoneLabel: 'IST',
    error: form.cancel_bucket_error,
    bookedAt: booked_at
  };
  return (
    <>
      <h5>Edit Session Details</h5>
      <CustomSingleSelect
        label="Change status"
        options={session_status_values}
        onChange={on_field}
        value={form.status || ''}
        name="status"
      />
      {is_group ? (
        <>
          {form.status === 'CANCELLED' ? (
            <div role="radiogroup" aria-label="Who cancelled" className="myt-form-radios">
              {group_options.map((option) => (
                <CustomRadioButton
                  key={option}
                  label={GROUP_CANCEL_LABELS[option]}
                  onRadioClick={() => set(group_cancel_pick_patch(form, option))}
                  selected={form.cancel_pick === option}
                />
              ))}
            </div>
          ) : null}
          {shows_student_app_cancel(
            form,
            session_details.session_type,
            session_details.session_status,
            session_details
          ) ? (
            <p className="myt-form-help">
              {stored_student_cancel_note(session_details.session_type)}
            </p>
          ) : null}
          {form.status === 'CANCELLED' && form.cancel_initiator_error ? (
            <p className="myt-form-error">
              * Choose who cancelled:{' '}
              {group_options.map((option) => GROUP_CANCEL_LABELS[option]).join(' or ')}
            </p>
          ) : null}
          {form.status === 'CANCELLED' && form.cancel_pick === GROUP_CANCEL_TEACHER ? (
            <TeacherCancelBucketPicker
              requestedAt={form.teacher_cancel_requested_at}
              onRequestedAtChange={(requested_at) =>
                set({ teacher_cancel_requested_at: requested_at, cancel_bucket_error: false })
              }
              {...picker}
            />
          ) : null}
        </>
      ) : (
        <>
          {form.status === 'CANCELLED' ? (
            <div role="radiogroup" aria-label="Who cancelled" className="myt-form-radios">
              {cancel_options.map((option) => (
                <CustomRadioButton
                  key={option}
                  label={
                    option === 'cancelled_by_student'
                      ? 'Cancelled by Student'
                      : 'Cancelled by Teacher'
                  }
                  onRadioClick={() => setForm((f) => ({ ...f, ...toggle_cancel_patch(f, option) }))}
                  selected={form[option]}
                />
              ))}
            </div>
          ) : null}
          {form.status === 'CANCELLED' && form.cancel_initiator_error ? (
            <p className="myt-form-error">
              * Choose who asked for the cancellation: Cancelled by Student or Cancelled by Teacher
            </p>
          ) : null}
          {form.status === 'CANCELLED' && form.cancelled_by_teacher ? (
            care_owns_time ? (
              <p className="myt-form-help">{CARE_OWNS_CANCEL_TIME_NOTE}</p>
            ) : capture ? (
              // the single time field is the Care draft's "Requested at (IST)" below
              <TeacherCancelBucketPicker requestedAt={care.draft.requested_at} {...picker} />
            ) : (
              <TeacherCancelBucketPicker
                requestedAt={form.teacher_cancel_requested_at}
                onRequestedAtChange={(requested_at) =>
                  set({ teacher_cancel_requested_at: requested_at, cancel_bucket_error: false })
                }
                required={is_new_teacher_cancel(form, session_details)}
                {...picker}
              />
            )
          ) : null}
        </>
      )}
      {capture ? (
        <CareRequestFields
          draft={care.draft}
          errors={care.errors}
          onChange={patch_care}
          showInitiatedBy={false}
          bookedAt={booked_at}
        />
      ) : null}
      <CustomCheckBox
        label="Ignore Rating"
        checked={ignore_rating}
        onClick={props.on_toggle_ignore}
      />
      {ignore_rating ? (
        <div className="myt-field">
          <textarea
            value={ignore_rating_reason}
            name="ignore_rating_reason"
            onChange={on_ignore_reason}
            style={{ height: '100px' }}
            maxLength="200"
            placeholder="Enter ignore rating reason (max 255 char)"
          />
          {form.rating_error ? (
            <p className="myt-form-error">* Ignore reason cannot be empty</p>
          ) : null}
        </div>
      ) : null}
      <EditReasonField
        edit={edit}
        value={form.status_change_reason}
        name="status_change_reason"
        onChange={on_field}
        error={form.status_reason_error}
        label={
          shows_cancel_email_note({
            session_type: session_details.session_type,
            status: form.status,
            sent_cancelled_by_teacher: is_group
              ? group_cancel_flags_sent(form.status === 'CANCELLED' ? form.cancel_pick : '').teacher
              : teacher_flag_sent(cancel_flags_to_send(form, session_details, care_owns_time)),
            saved_cancelled_by_teacher: session_details.cancelled_by_teacher
          })
            ? CANCEL_EMAIL_NOTE_LABEL
            : ''
        }
      />
      {form.save_modal_error ? <p className="myt-form-error">* {form.save_modal_error}</p> : null}
      <FormActions>
        <Button onClick={() => save()}>Save</Button>
      </FormActions>
    </>
  );
}

EditSessionForm.propTypes = {
  session_uuid: PropTypes.string,
  session_details: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  ignore_rating: PropTypes.bool,
  ignore_rating_reason: PropTypes.string,
  on_toggle_ignore: PropTypes.func.isRequired,
  on_page_field_change: PropTypes.func.isRequired,
  set_loading: PropTypes.func.isRequired,
  on_saved: PropTypes.func.isRequired,
  on_save_failed: PropTypes.func.isRequired,
  on_close: PropTypes.func.isRequired,
  on_care_saved: PropTypes.func.isRequired,
  on_retry_change: PropTypes.func.isRequired,
  notify: PropTypes.func.isRequired
};

EditSessionForm.defaultProps = {
  session_uuid: '',
  loading: false,
  ignore_rating: false,
  ignore_rating_reason: ''
};

export default EditSessionForm;
